// @flow
import { noop } from 'lodash';
import React, { createContext, FunctionComponent, PropsWithChildren, useCallback, useMemo } from 'react';
import { useSetState } from 'react-use';
import VirtualComponentForm from '../components/virtual-components/VirtualComponentForm';

export type ContextType = {
    show: (onSuccessCallback?: Function) => void,
};

export const Context = createContext<ContextType>({
    show: noop,
});

type State = {
    opened?: boolean,
    onSuccess?: Function,
};

const initialState = {
    opened: false,
    onSuccess: null,
};

const ContextProvider: FunctionComponent<PropsWithChildren<unknown>> = ({ children }) => {
    const [{ opened, onSuccess }, setState] = useSetState<State>(initialState);

    const close = useCallback(() => setState(initialState), [setState]);

    const providerValue = useMemo(
        () => ({
            show: (onSuccessCallback) => setState({ opened: true, onSuccess: onSuccessCallback }),
        }),
        [setState],
    );

    return (
        <Context.Provider value={providerValue}>
            {opened && (
                <VirtualComponentForm setIsOpen={close} skipVirtualComponentQueryRefetch onSuccess={onSuccess} />
            )}
            {children}
        </Context.Provider>
    );
};

export const withCreateVirtualComponentContext = (Component) => {
    const WithCreateVirtualComponentContext = (props) => (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );
    return WithCreateVirtualComponentContext;
};

export default ContextProvider;
